import { useRef, useState } from "react";
import { LuPencilLine, LuTrash2, LuUpload, LuDownload } from "react-icons/lu";
import { PiCheckSquareOffset } from "react-icons/pi";
import SelectionActionBar from "../../../common/SelectionActionBar";
import ConfirmDialog from "../../../common/ConfirmDialog";
import InventoryFormModal from "./InventoryFormModal";

export default function InventoryActionBar({
  selectedRow,
  handleDelete,
  handleEdit,
  handleSelectAll,
  handleExport,
  handleImport,
}) {
  const selectedCount = selectedRow.size;
  const selectedIds = Array.from(selectedRow.keys());
  const singleSelected =
    selectedCount === 1 ? { id: selectedIds[0], ...selectedRow.get(selectedIds[0]) } : null;

  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);

  const openEditModal = () => {
    if (!singleSelected) return;
    setFormOpen(true);
  };

  const handleFormSubmit = (values) => {
    handleEdit(values);
    setFormOpen(false);
  };

  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    if (e.target.files?.length) handleImport(e.target);
    e.target.value = "";
  };

  const handleConfirmDelete = () => {
    handleDelete();
    setDeleteConfirmOpen(false);
  };

  const actions = [
    selectedCount === 1 && { key: "edit", label: "Edit", icon: LuPencilLine, onClick: openEditModal },
    selectedCount >= 1 && { key: "delete", label: "Delete", icon: LuTrash2, onClick: () => setDeleteConfirmOpen(true), danger: true },
  ];

  return (
    <>
      {/* Always-visible toolbar — hidden while a selection bar is showing so they never overlap */}
      {selectedCount === 0 && (
        <div className="fixed inset-x-0 bottom-4 z-30 flex justify-center px-3">
          <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white/95 p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.14)] backdrop-blur">
            <button
              onClick={handleImportClick}
              title="Import CSV"
              className="flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
            >
              <LuUpload size={15} />
              <span className="hidden md:inline">Import</span>
            </button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />

            <button
              onClick={handleExport}
              title="Export CSV"
              className="flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
            >
              <LuDownload size={15} />
              <span className="hidden md:inline">Export</span>
            </button>

            <button
              onClick={handleSelectAll}
              title="Select all"
              className="flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
            >
              <PiCheckSquareOffset size={15} />
              <span className="hidden md:inline">Select All</span>
            </button>
          </div>
        </div>
      )}

      <SelectionActionBar selectedCount={selectedCount} actions={actions} onClear={handleSelectAll} />

      <InventoryFormModal
        open={formOpen}
        mode="edit"
        initialValue={singleSelected}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={selectedCount === 1 ? "Delete item?" : "Delete items?"}
        description={`This will permanently delete ${selectedCount} inventory item${
          selectedCount > 1 ? "s" : ""
        }. This action cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
      />
    </>
  );
}